import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  ECookieProcessingServices,
  TCookieProcessingServices,
  TUCConsentEventDetails,
} from '~/constants/cmp';
import { useOnConsentChange } from '~/hooks/useOnConsentChange';

export default function useCookieConsents(): {
  consents: Partial<TCookieProcessingServices>;
  acceptService: (service: ECookieProcessingServices) => Promise<void>;
} {
  const dispatch = useDispatch();
  const onConsentChange = useOnConsentChange();
  const CMPInitCallbacks = useRef<Array<() => void | Promise<void>>>([]);
  const [consents, setConsents] = useState<Partial<TCookieProcessingServices>>(
    {},
  );

  useEffect(() => {
    const onCMPInit = () => {
      while (CMPInitCallbacks.current.length > 0) {
        CMPInitCallbacks.current.shift()?.();
      }
    };
    window.addEventListener('UC_UI_INITIALIZED', onCMPInit);
    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', onCMPInit);
    };
  }, []);

  useEffect(() => {
    const consentStatusHandler = (e: CustomEvent<TUCConsentEventDetails>) => {
      const { action, event, type, ...serviceConsents } = e.detail;
      onConsentChange(serviceConsents);
      setConsents(serviceConsents);
    };
    window.addEventListener('ucEvent', consentStatusHandler);

    return () => {
      window.removeEventListener('ucEvent', consentStatusHandler);
    };
  }, [dispatch, onConsentChange]);

  const acceptService = useCallback(
    async (service: ECookieProcessingServices) => {
      if (!window.UC_UI?.isInitialized?.()) {
        CMPInitCallbacks.current.push(() => acceptService(service));
        return;
      }
      const allServicesInfo = window.UC_UI.getServicesBaseInfo();
      const selectedServiceInfo = allServicesInfo.find(
        (serviceInfo) => serviceInfo.name === service,
      );
      if (selectedServiceInfo?.id) {
        await window.UC_UI.acceptService(selectedServiceInfo.id);
      } else {
        // maybe send a manual log to Sentry?
        throw new Error(`${service} cannot be found in services list`);
      }
    },
    [],
  );

  return useMemo(
    () => ({ consents, acceptService }),
    [consents, acceptService],
  );
}
